/**
 * Use Global Tabs Hook Implementation
 *
 * Custom hook for fetching and managing global tabs data from API.
 * This hook is only responsible for data fetching and local state management.
 *
 * @since 1.0.0
 */

import apiFetch from '@wordpress/api-fetch';
import { __ } from '@wordpress/i18n';
import { useCallback, useEffect, useState } from 'react';

import type { GlobalTab } from '../../components/global-tabs/types';
import type {
  RedFoxTab,
  RedFoxTabsParams,
  UseGlobalTabsParams,
  UseGlobalTabsReturn,
} from './types';

/**
 * Custom hook for managing global tabs data
 *
 * @since 1.0.0
 *
 * @param params Hook parameters.
 *
 * @return Hook return value.
 */
export const useGlobalTabs = (params: UseGlobalTabsParams = {}): UseGlobalTabsReturn => {
  const { autoLoad = true, onError } = params;

  const [tabs, setTabs] = useState<GlobalTab[]>([]);
  const [originalTabs, setOriginalTabs] = useState<GlobalTab[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Check if there are unsaved changes
  const hasUnsavedChanges = JSON.stringify(tabs) !== JSON.stringify(originalTabs);

  /**
   * Show error notification
   *
   * @since 1.0.0
   *
   * @param message Error message.
   */
  const showError = useCallback(
    (message: string) => {
      setError(message);
      if (onError) {
        onError(message);
      }
    },
    [onError]
  );

  /**
   * Fetch global tabs from API
   *
   * @since 1.0.0
   */
  const fetchTabs = useCallback(async (): Promise<void> => {
    try {
      setLoading(true);
      setError(null);

      const response = await apiFetch<{ success: boolean; data: GlobalTab[] }>({
        path: '/redfox-tab-manager/v1/global-tabs',
        method: 'GET',
      });

      // Extract data from API response wrapper
      const tabsData = response?.data ?? response;
      // Ensure response is always an array
      const tabsArray = Array.isArray(tabsData) ? tabsData : [];

      setTabs(tabsArray);
      setOriginalTabs(tabsArray);
    } catch {
      const errorMessage = __(
        'Failed to load global tabs. Please try again.',
        'redfox-tab-manager'
      );
      showError(errorMessage);
      setTabs([]);
      setOriginalTabs([]);
    } finally {
      setLoading(false);
    }
  }, [showError]);

  /**
   * Update tabs without saving
   *
   * @since 1.0.0
   *
   * @param newTabs New tabs array.
   */
  const updateTabs = useCallback((newTabs: GlobalTab[]): void => {
    setTabs(newTabs);
  }, []);

  /**
   * Reset tabs to original state
   *
   * @since 1.0.0
   */
  const resetTabs = useCallback((): void => {
    setTabs(originalTabs);
  }, [originalTabs]);

  /**
   * Fetch RedFox tabs (CPT)
   *
   * @since 1.0.0
   *
   * @param params Fetch parameters.
   *
   * @return Array of RedFox tabs.
   */
  const fetchRedFoxTabs = useCallback(
    async (fetchParams: RedFoxTabsParams = {}): Promise<RedFoxTab[]> => {
      try {
        const { postsPerPage = -1, orderBy = 'date', order = 'DESC' } = fetchParams;

        const queryParams = new URLSearchParams({
          posts_per_page: postsPerPage.toString(),
          orderby: orderBy,
          order,
          include_content: 'true',
        });

        const apiUrl = `/redfox-tab-manager/v1/available-tabs?${queryParams.toString()}`;

        const response = await apiFetch<{ success: boolean; data: RedFoxTab[] }>({
          path: apiUrl,
        });

        // Extract data from API response wrapper
        const tabsData = response?.data ?? response;
        return Array.isArray(tabsData) ? tabsData : [];
      } catch {
        const errorMessage = __(
          'Failed to fetch RedFox tabs. Please try again.',
          'redfox-tab-manager'
        );
        showError(errorMessage);
        return [];
      }
    },
    [showError]
  );

  // Auto-load tabs on mount
  useEffect(() => {
    if (autoLoad) {
      fetchTabs();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    tabs,
    loading,
    error,
    hasUnsavedChanges,
    fetchTabs,
    updateTabs,
    resetTabs,
    fetchRedFoxTabs,
  };
};
